import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Svg } from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';

type PaginatorProps = {
  currentPageNumber: number;
  goToFirst: () => void;
  goToLast: () => void;
  lastPageNumber: number;
  goToPage: (pageNumber: number) => void;
  next: () => void;
  previous: () => void;
  disableNext?: boolean;
  disablePrevious?: boolean;
};

export const Paginator: FC<PaginatorProps> = ({
  currentPageNumber,
  goToFirst,
  goToLast,
  lastPageNumber,
  goToPage,
  next,
  previous,
  disableNext = false,
  disablePrevious = false,
}: PaginatorProps) => {
  const pagesArray = useMemo(() => {
    const NUM_3 = 3;
    const NUM_4 = 4;
    const NUM_5 = 5;
    let startPage = Math.max(1, currentPageNumber - 2);
    let endPage = Math.min(lastPageNumber, startPage + NUM_4);

    if (currentPageNumber <= NUM_3 && lastPageNumber >= NUM_5) {
      endPage = 5;
    } else if (endPage - startPage < NUM_4 && lastPageNumber >= startPage + NUM_4) {
      startPage = endPage - NUM_4;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(i => ({
      pageNumber: i,
      isActive: i === currentPageNumber,
    }));
  }, [lastPageNumber, currentPageNumber]);

  const t = useTranslations();
  const mode = useSelector(getModeTheme());
  const color = mode === 'dark' ? 'rgb(50, 56, 64)' : '#444547';

  if (lastPageNumber < 2) {
    return null;
  }

  return (
    <Box display='flex' justifyContent='end' alignItems='center' mt='s'>
      <Typography
        variant='p2/light'
        pb='xs'
        px='xxs'
        pt='s'
        cursor={disablePrevious ? 'not-allowed' : 'pointer'}
        opacity={disablePrevious ? 0.4 : 1}
        onClick={disablePrevious ? undefined : goToFirst}
      >
        <Svg name='chevronsLeft' size='xs' />
      </Typography>
      <Typography
        variant='p2/light'
        pb='xs'
        px='xxs'
        pt='s'
        cursor={disablePrevious ? 'not-allowed' : 'pointer'}
        opacity={disablePrevious ? 0.4 : 1}
        onClick={disablePrevious ? undefined : previous}
      >
        <Svg name='chevronLeft' size='xs' />
      </Typography>
      {pagesArray.map(page => (
        <Typography
          variant={page.isActive ? 'p2/bold' : 'p2/light'}
          py='xxs'
          px='xs'
          cursor='pointer'
          borderRadius='m'
          onClick={() => goToPage(page.pageNumber)}
          key={page.pageNumber}
          background={page.isActive ? color : ''}
        >
          {page.pageNumber}
        </Typography>
      ))}
      <Typography variant='p2/light' py='xxs' px='xxs' color='grey/light'>
        {t('table.ofPages', { total: lastPageNumber })}
      </Typography>
      <Typography
        variant='p2/light'
        pb='xs'
        px='xxs'
        pt='s'
        cursor={disableNext ? 'not-allowed' : 'pointer'}
        opacity={disableNext ? 0.4 : 1}
        onClick={disableNext ? undefined : next}
      >
        <Svg name='chevronRight' size='xs' />
      </Typography>
      <Typography
        variant='p2/light'
        pb='xs'
        px='xxs'
        pt='s'
        cursor={disableNext ? 'not-allowed' : 'pointer'}
        opacity={disableNext ? 0.4 : 1}
        onClick={disableNext ? undefined : goToLast}
      >
        <Svg name='chevronsRight' size='xs' />
      </Typography>
    </Box>
  );
};
